<template>
  <div @mouseenter="openDrawer" @mouseleave="closeDrawer">
    <nav :class="navClasses">
      <div v-memo>
        <img src="@/public/images/logo-collapsed.png" alt="Logo" height="32px" v-show="!drawer" />
        <img src="@/public/images/logo-expanded.png" alt="Logo" height="32px" v-show="drawer" />
      </div>

      <div class="sidebar-box">
        <div class="sidebar-box-start">
          <div v-for="(item, index) in pageList" :key="index">
            <s-sidebar-item v-if="!item?.tabs" :item="item" :drawer="drawer" :goToPage="goToPage" />
            
            <s-sidebar-group v-else :item="item" :user="user!" :drawer="drawer" :toggleGroup="toggleGroup" :goToPage="goToPage" />
          </div>
        </div>

        <div class="sidebar-box-end">
          <s-sidebar-theme :drawer="drawer" />
          <s-sidebar-user :drawer="drawer" :user="user!" :logout="logout" />
        </div>
      </div>
    </nav>
  </div>
</template>

<script lang="ts" setup>
import { SYSTEM_PAGES } from '~/constants/page';
import { useAuthStore } from '~/store/auth';

const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

const router = useRouter();
const route = useRoute();

const drawer = ref(false);
const pageList = ref<Page[]>([]);

// COMPUTED

const navClasses = computed(() => ({
  'sidebar': true,
  'expanded': drawer.value
}));

// METHODS

const openDrawer = () => drawer.value = true;

const closeDrawer = () => {
  pageList.value.forEach(item => item.expanded = false);
  drawer.value = false;
};

const goToPage = (...routes: string[]) => {
  const paths = routes.filter(Boolean).join('/');
  router.push(`/${paths}`);
};

const setPages = () => {
  if (!user.value) return;

  const availablePages = SYSTEM_PAGES.filter(page => page.sidebar).map(page => ({ ...page, expanded: false }));
  
  pageList.value = getAllowedPages(availablePages);
};

const updateActiveState = () => {
  setPages();
  const currentRoute = route.path  == '/' ? route.path :  `/${route.path.split("/").filter(Boolean)[0]}`;  
  pageList.value.forEach(item => item.active = currentRoute === item.path || item.tabs?.some(tab => currentRoute === tab.path));
};

const toggleGroup = (item: Page) => {
  pageList.value.forEach(i => { if (i !== item) i.expanded = false; });
  item.expanded = !item.expanded;
};

const logout = () => authStore.logout();

// LYFECYCLE

onMounted(updateActiveState);

watch(() => route.path, updateActiveState, { immediate: true });

watch(user, (value, oldValue) => {
  if (value !== oldValue) {
    setPages();
    updateActiveState();
  }
}, { immediate: true });

</script>

<style>
.sidebar {
  width: 44px;
  height: calc(100vh - 20px);
  position: fixed;
  top: 0;
  left: 0;
  overflow-y: auto;
  overflow-x: hidden;
  transition: width var(--ts-fast);
  background-color: var(--bg-primary);
  border-right: 1px solid var(--border-primary);
  color: var(--text-primary);
  padding: 10px;
  z-index: 999;
}
.sidebar.expanded {
  width: 250px;
}
.sidebar img {
  display: flex;
  margin: auto;
  cursor: pointer;
}
.sidebar-box {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: calc(100vh - 65px);
}
.sidebar-box-start {
  border-bottom: 1px solid var(--border-primary);
  border-top: 1px solid var(--border-primary);
  margin: 10px 0px;
  padding: 10px 0px;
  overflow-y: auto;
  overflow-x: hidden;
}
.sidebar-box-end {
  border-top: 1px solid var(--border-primary);
}
.sidebar-item {
  width: calc(100% - 20px);
  padding: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: row;
  gap: 10px;
  transition: background-color 0.3s;
  border-radius: 4px;
}
.sidebar-item.selected {
  background-color: var(--bg-accent);
}
.sidebar-item.expanded {
  background-color: var(--bg-accent-secondary);
}
.sidebar-item:hover {
  background-color: var(--bg-accent-hover);
  color: var(--primary);
}
.sidebar-item-text {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  gap: 10px;
}
.sidebar-sub-items {
  padding-left: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sidebar-sub-item {
  padding: 10px;
  cursor: pointer;
  transition: background-color 0.3s;
  border-radius: 4px;
}
.sidebar-sub-item.selected {
  background-color: var(--bg-accent-secondary);
}
.sidebar-sub-item:hover {
  background-color: var(--bg-accent-hover);
  color: var(--primary);
}
.sidebar-user {
  height: 24px;
  width: calc(100% - 20px);
  padding: 20px 5px 0px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-direction: row;
  gap: 10px;
}
.sidebar-user-text {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
  gap: 10px;
}
</style>
